// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { ProjectStatus } from '@/constants/project-status';
import { ProjectAssetAssignment } from '@/project/api/types';
import { formatNumber } from '@/utilities/number';
import { Box, Typography, TypographyProps } from '@mui/material';
import { Fragment } from 'react';

interface Props extends Pick<TypographyProps, 'variant'> {
  /**
   * The status of the project.
   */
  status: ProjectStatus;

  /**
   * A list of product configurations for a project.
   */
  assignments: ProjectAssetAssignment[];
}

/**
 * A label component that renders a project's product configurations or an empty
 * state if there are no products.
 */
export function ProjectProductsLabel(props: Props) {
  if (props.assignments.length === 0) {
    return (
      <Typography variant={props.variant} color="text.secondary">
        Not set
      </Typography>
    );
  }

  const hasDeltas = props.assignments.some((assignment) => {
    const delta = assignment.requestedCount - assignment.assignedCount;

    return Math.abs(delta) > 0;
  });

  const showDelta =
    hasDeltas &&
    (props.status === 'ACTIVE' || props.status === 'DATE_RANGE_INCOMPLETE');

  const gridTemplateColumns = showDelta
    ? 'repeat(3, auto)'
    : 'auto min-content';

  return (
    <Box display="grid" gridTemplateColumns={gridTemplateColumns} columnGap={1}>
      {props.assignments.map((assignment) => {
        const delta = assignment.requestedCount - assignment.assignedCount;
        const label = delta > 0 ? 'needed' : 'extra';

        const displayName = assignment.sku
          ? `${assignment.sku} (${assignment.name})`
          : assignment.name;

        return (
          <Fragment key={assignment.id}>
            <Typography variant={props.variant}>{displayName}</Typography>
            <Typography variant={props.variant} whiteSpace="nowrap">
              {formatNumber(assignment.assignedCount)} /{' '}
              {formatNumber(assignment.requestedCount)}
            </Typography>
            {showDelta && (
              <Typography variant={props.variant}>
                {Math.abs(delta) > 0
                  ? `(${formatNumber(Math.abs(delta))} ${label})`
                  : null}
              </Typography>
            )}
          </Fragment>
        );
      })}
    </Box>
  );
}
